import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { C, STATES } from './constants';
import { castVote, cacheVote, fetchCounts, isResultsPublished } from './storage';
import { getCycleEnd } from './utils';
import { setMeta } from './utils/meta';
import { useCountdown } from './hooks';
import ErrorBoundary from './components/ErrorBoundary';
import FontStyles from './components/FontStyles';
import PageWrapper from './components/PageWrapper';
import SiteFooter from './components/SiteFooter';
import SiteHeader from './components/SiteHeader';
import StatePickerScreen from './screens/StatePickerScreen';
import LandingScreen from './screens/LandingScreen';
import VotingScreen from './screens/VotingScreen';
import UndoScreen from './screens/UndoScreen';
import PostVoteScreen from './screens/PostVoteScreen';
import ResultsPendingScreen from './screens/ResultsPendingScreen';
import BlogListScreen from './screens/BlogListScreen';
import BlogPostScreen from './screens/BlogPostScreen';
import BlogPageWrapper from './components/BlogPageWrapper';
import StaticPageWrapper from './components/StaticPageWrapper';
import AboutScreen from './screens/AboutScreen';
import PrivacyScreen from './screens/PrivacyScreen';
import FAQScreen from './screens/FAQScreen';
import HowItWorksScreen from './screens/HowItWorksScreen';
import ElectionCalendarScreen from './screens/ElectionCalendarScreen';
import SevenStates2027Screen from './screens/SevenStates2027Screen';
import ContactScreen from './screens/ContactScreen';
import CJPApp from './screens/CJPApp';

function readLocalVote(stateId) {
  try {
    const v = localStorage.getItem(`${stateId}:user_vote`);
    return v ? JSON.parse(v) : null;
  } catch (_) { return null; }
}

// ── Inner app — reads :stateId param ───────────────────────────────────────
function AppInner() {
  const { stateId }  = useParams();          // undefined on "/"
  const navigate     = useNavigate();

  // Read localStorage synchronously — no network call needed on startup.
  // If localStorage was cleared, the duplicate-vote check at cast time handles it.
  const [screen,            setScreen]            = useState('statePicker');
  const [activeState,       setActiveState]       = useState(null);
  const [votedStates,       setVotedStates]       = useState(() => ({
    tn:  readLocalVote('tn'),
    wb:  readLocalVote('wb'),
  }));
  const [district,          setDistrict]          = useState('');
  const [party,             setParty]             = useState('');
  const [counts,            setCounts]            = useState({});
  const [userVote,          setUserVote]          = useState(null);
  const [published,         setPublished]         = useState(false);
  const [votingClosed,      setVotingClosed]      = useState(false);
  const [undoLeft,          setUndoLeft]          = useState(5);
  const [confetti,          setConfetti]          = useState(false);
  const [confettiColor,     setConfettiColor]     = useState(null);
  const [showAll,           setShowAll]           = useState(false);
  const [highlightDistrict, setHighlightDistrict] = useState(null);
  const [error,             setError]             = useState('');

  const undoTimerRef = useRef(null);
  const cycleEnd     = useMemo(() => getCycleEnd(), []);
  const countdown    = useCountdown(cycleEnd);

  // ── Core: load a state's data ────────────────────────────────────────────
  const loadState = useCallback(async (sid, votes) => {
    const state = STATES[sid];
    if (!state) { navigate('/'); return; }

    setActiveState(state);
    setError('');
    setShowAll(false);
    setDistrict('');
    setParty('');
    setScreen('loading');

    const existingVote = votes[sid];

    // Always check published status — affects both voters and non-voters
    const [pub, vc] = await Promise.all([
      isResultsPublished(sid).catch(() => false),
      existingVote || false ? fetchCounts(sid).catch(() => ({})) : Promise.resolve({}),
    ]);
    setPublished(pub);
    setVotingClosed(pub);

    if (existingVote) {
      setCounts(vc);
      setUserVote(existingVote);
      setHighlightDistrict(existingVote.district);
      setScreen('postVote'); // voters always see results immediately
    } else if (pub) {
      // Results published — fetch counts and show results even for non-voters
      const counts = await fetchCounts(sid).catch(() => ({}));
      setCounts(counts);
      setUserVote(null);
      setHighlightDistrict(null);
      setScreen('postVote');
    } else {
      setUserVote(null);
      setHighlightDistrict(null);
      setScreen('landing');
    }
  }, [navigate]);

  // Keep a ref so the URL-change effect always sees the latest votedStates
  const votedStatesRef = useRef(votedStates);
  useEffect(() => { votedStatesRef.current = votedStates; }, [votedStates]);

  // ── Respond to URL changes (browser back/forward + first mount) ───────────
  useEffect(() => {
    if (!stateId) {
      // URL is "/" — show state picker
      clearInterval(undoTimerRef.current);
      setActiveState(null);
      setScreen('statePicker');
      return;
    }
    if (!STATES[stateId]) { navigate('/'); return; }
    // Only load if the state in the URL differs from what's currently active.
    // This avoids resetting mid-flow (e.g. landing → voting) when the URL
    // hasn't actually changed — just the screen state has.
    if (!activeState || activeState.id !== stateId) {
      loadState(stateId, votedStatesRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateId]); // intentionally only stateId — reacts to URL, not internal state

  // ── Select state from picker — navigates to /:stateId ────────────────────
  const selectState = useCallback((sid) => {
    if (sid === 'cjp') { navigate('/cockroach-janata-party-elections'); return; }
    navigate(`/${sid}`);
    loadState(sid, votedStates);
  }, [navigate, loadState, votedStates]);

  const goToStatePicker = useCallback(() => {
    clearInterval(undoTimerRef.current);
    setActiveState(null);
    navigate('/');
    setScreen('statePicker');
  }, [navigate]);

  // ── Dynamic page title + meta tags ──────────────────────────────────────
  useEffect(() => {
    const sid = activeState?.id;

    // State-specific meta lookup
    const STATE_META = {
      tn: {
        title:       'Tamil Nadu Election 2026 - Unofficial Poll | Live District Results',
        description: 'See how Tamil Nadu voted in 2026. Live results across 38 districts, 10 parties. TVK, DMK, AIADMK and more. Free anonymous poll on UnofficialElections.',
        ogTitle:     'Tamil Nadu 2026 - Live Unofficial Election Results',
        ogDesc:      '38 districts. 10 parties. See the live map. Cast your anonymous vote.',
        ogImage:     '/og-tn.png',
        canonical:   '/tn/',
      },
      wb: {
        title:       'West Bengal Election 2026 - Unofficial Poll | Live District Results',
        description: 'West Bengal 2026 election results on an interactive district map. 23 districts, 6 parties. TMC, BJP, Left and more. Free anonymous voting.',
        ogTitle:     'West Bengal 2026 - Live Unofficial Election Results',
        ogDesc:      '23 districts. TMC vs BJP vs Left. See the live map.',
        ogImage:     '/og-wb.png',
        canonical:   '/wb/',
      },
      cjp: {
        title:       'Mock 2029 Lok Sabha - India National Poll | UnofficialElections',
        description: 'Who would India vote for in 2029? Cast your anonymous mock vote in the CJP national ballot. 36 states and UTs, 10 parties — BJP, INC, AAP, CJP and more.',
        ogTitle:     'India Unofficial Election - Vote Now',
        ogDesc:      '36 states/UTs. 10 parties. Who does India vote for? Cast your vote.',
        ogImage:     '/og-cjp.png',
        canonical:   '/cockroach-janata-party-elections/',
      },
    };

    const HOME_META = {
      title:       "UnofficialElections - India's Free Anonymous Election Polling Platform",
      description: 'Cast your anonymous vote in Indian state elections. See live district-level results. No registration, no data collected. TN, WB live — UP, Punjab, Gujarat coming 2027.',
      ogTitle:     "UnofficialElections - India's Free Anonymous Election Poll",
      ogDesc:      'Cast your vote. See live district results. 100% anonymous. Tracking elections across India.',
      ogImage:     '/og-home.png',
      canonical:   '/',
    };

    if (screen === 'statePicker' || !activeState) {
      setMeta({
        title:         HOME_META.title,
        description:   HOME_META.description,
        ogTitle:       HOME_META.ogTitle,
        ogDescription: HOME_META.ogDesc,
        ogImage:       HOME_META.ogImage,
        canonical:     HOME_META.canonical,
      });
      return;
    }

    const sm = STATE_META[sid];
    if (sm) {
      const suffix = {
        landing:        '— Cast Your Vote',
        voting:         '— Ballot',
        undo:           '— Confirm Vote',
        postVote:       '— Live Results',
        resultsPending: '— Results Pending',
      }[screen] || '';

      setMeta({
        title:         suffix ? `${sm.title.split('|')[0].trim()} ${suffix}` : sm.title,
        description:   sm.description,
        ogTitle:       sm.ogTitle,
        ogDescription: sm.ogDesc,
        ogImage:       sm.ogImage,
        canonical:     sm.canonical,
      });
    } else {
      // Fallback for any future state without a meta entry
      const stateName = activeState.name;
      setMeta({
        title:       `${stateName} Election - Unofficial Poll | UnofficialElections`,
        description: `Live unofficial election poll results for ${stateName}. Anonymous, free, no registration.`,
        ogImage:     '/og-home.png',
        canonical:   `/${sid}/`,
      });
    }
  }, [screen, activeState]);

  // ── Live refresh — all post-landing screens poll for published status ─────
  useEffect(() => {
    const watched = ['postVote', 'resultsPending', 'landing', 'voting'];
    if (!watched.includes(screen) || !activeState) return;

    const refresh = async () => {
      const pub = await isResultsPublished(activeState.id).catch(() => false);
      setPublished(pub);
      setVotingClosed(pub);

      if (pub) {
        // Results just went live — fetch counts and push everyone to results
        if (screen !== 'postVote') {
          const vc = await fetchCounts(activeState.id).catch(() => ({}));
          setCounts(vc);
          setScreen('postVote');
        } else {
          // Already on postVote — just refresh counts
          const vc = await fetchCounts(activeState.id).catch(() => ({}));
          setCounts(vc);
        }
      }
    };

    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [screen, activeState, userVote]);

  // ── Commit vote ───────────────────────────────────────────────────────────
  const commitVote = useCallback(async () => {
    if (!activeState) return;
    const vote = { district, party, ts: Date.now() };
    const partyColor = activeState.partyById[party]?.color || C.cyan;

    setScreen('submitting');

    try {
      const result = await castVote(activeState.id, district, party);

      if (result.ok || result.duplicate) {
        cacheVote(activeState.id, vote);
        setUserVote(vote);
        setVotedStates(prev => ({ ...prev, [activeState.id]: vote }));
        setHighlightDistrict(district);
        setConfettiColor(partyColor);
        setConfetti(true);
        setTimeout(() => setConfetti(false), 4500);

        const [pub, vc] = await Promise.all([
          isResultsPublished(activeState.id).catch(() => false),
          fetchCounts(activeState.id).catch(() => ({})),
        ]);
        setPublished(pub);
        setCounts(vc);
        setScreen('postVote'); // voters always see results immediately
      } else {
        setError(`Could not save your vote: ${result.error || 'unknown error'}. Please try again.`);
        setScreen('voting');
      }
    } catch (e) {
      console.error('[commitVote] unexpected error:', e);
      setError('Something went wrong. Please check your connection and try again.');
      setScreen('voting');
    }
  }, [activeState, district, party]);

  const commitVoteRef = useRef(commitVote);
  useEffect(() => { commitVoteRef.current = commitVote; }, [commitVote]);

  // ── Undo countdown ────────────────────────────────────────────────────────
  useEffect(() => {
    if (screen !== 'undo') return;
    setUndoLeft(5);
    const start = Date.now();
    undoTimerRef.current = setInterval(() => {
      const left = Math.max(0, 5 - (Date.now() - start) / 1000);
      setUndoLeft(left);
      if (left <= 0) {
        clearInterval(undoTimerRef.current);
        commitVoteRef.current();
      }
    }, 50);
    return () => clearInterval(undoTimerRef.current);
  }, [screen]);

  const cancelVote = useCallback(() => {
    clearInterval(undoTimerRef.current);
    setScreen('voting');
  }, []);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (screen === 'loading' && !activeState) {
    return (
      <div style={{
        background: C.bg, minHeight: '100vh', color: C.white,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Poppins', sans-serif",
      }}>
        <FontStyles />
        <span style={{ color: C.textDim, fontSize: '0.95rem' }}>Loading…</span>
      </div>
    );
  }

  // ── State picker ──────────────────────────────────────────────────────────
  if (screen === 'statePicker') {
    return (
      <ErrorBoundary>
        <div style={{
          background: C.bg, minHeight: '100vh', color: C.white,
          fontFamily: "'Poppins', sans-serif",
          backgroundImage:
            'radial-gradient(ellipse at 0% 0%, rgba(0,229,255,0.10) 0%, transparent 45%), ' +
            'radial-gradient(ellipse at 100% 100%, rgba(255,77,158,0.10) 0%, transparent 45%)',
        }}>
          <FontStyles />
          <SiteHeader />
          <div className="relative z-10">
            <StatePickerScreen votedStates={votedStates} onSelect={selectState} />
          </div>
          <SiteFooter />
        </div>
      </ErrorBoundary>
    );
  }

  // ── State screens ─────────────────────────────────────────────────────────
  return (
    <ErrorBoundary>
      <PageWrapper
        confetti={confetti}
        confettiColor={confettiColor}
        onChangeState={goToStatePicker}
        countdown={countdown}
        stateData={activeState}
        published={published}
      >
        {(screen === 'loading' || screen === 'submitting') && (
          <div style={{
            minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: '0.75rem',
          }}>
            <span style={{ color: C.textDim, fontSize: '0.95rem' }}>
              {screen === 'submitting' ? 'Submitting your vote…' : 'Loading…'}
            </span>
          </div>
        )}
        {screen === 'landing' && (
          <LandingScreen stateData={activeState} onStart={() => setScreen('voting')} />
        )}
        {screen === 'voting' && (
          <VotingScreen
            stateData={activeState}
            district={district}
            setDistrict={setDistrict}
            party={party}
            setParty={setParty}
            onBack={() => setScreen('landing')}
            onCast={() => { if (district && party) setScreen('undo'); }}
            error={error}
          />
        )}
        {screen === 'undo' && (
          <UndoScreen
            stateData={activeState}
            district={district}
            party={party}
            undoLeft={undoLeft}
            onCancel={cancelVote}
          />
        )}
        {screen === 'resultsPending' && (
          <ResultsPendingScreen stateData={activeState} userVote={userVote} />
        )}
        {screen === 'postVote' && (
          <PostVoteScreen
            stateData={activeState}
            counts={counts}
            userVote={userVote}
            votingClosed={votingClosed}
            highlightDistrict={highlightDistrict}
            setHighlightDistrict={setHighlightDistrict}
            showAll={showAll}
            setShowAll={setShowAll}
            error={error}
          />
        )}
      </PageWrapper>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Blog */}
      <Route path="/blog/:slug" element={<BlogPageWrapper><BlogPostScreen /></BlogPageWrapper>} />
      <Route path="/blog"       element={<BlogPageWrapper><BlogListScreen /></BlogPageWrapper>} />
      {/* Static pages */}
      <Route path="/about"             element={<StaticPageWrapper><AboutScreen /></StaticPageWrapper>} />
      <Route path="/privacy"           element={<StaticPageWrapper><PrivacyScreen /></StaticPageWrapper>} />
      <Route path="/faq"               element={<StaticPageWrapper><FAQScreen /></StaticPageWrapper>} />
      <Route path="/how-it-works"      element={<StaticPageWrapper><HowItWorksScreen /></StaticPageWrapper>} />
      <Route path="/election-calendar" element={<StaticPageWrapper><ElectionCalendarScreen /></StaticPageWrapper>} />
      <Route path="/2027"              element={<StaticPageWrapper><SevenStates2027Screen /></StaticPageWrapper>} />
      <Route path="/contact"           element={<StaticPageWrapper><ContactScreen /></StaticPageWrapper>} />
      {/* CJP — standalone cream/risograph page */}
      <Route path="/cockroach-janata-party-elections" element={<CJPApp />} />
      {/* App */}
      <Route path="/:stateId"   element={<AppInner />} />
      <Route path="/"           element={<AppInner />} />
    </Routes>
  );
}
